const API_BASE_URL = 'https://agrimocksbe.onrender.com/api';
const API_BASE_URL_LOCAL = 'http://localhost:5001/api';
const API_URL = API_BASE_URL;

export interface BlogPost {
  _id?: string;
  title: string;
  excerpt: string;
  content: string;
  author: string;
  publishedDate: string;
  category: string | { _id: string; name: string; slug: string };
  slug: string;
  status: 'draft' | 'published';
  image?: string;
  createdBy?: {
    _id: string;
    name: string;
    email: string;
  };
  createdAt?: string;
  updatedAt?: string;
}

export interface BlogResponse {
  success: boolean;
  data: BlogPost | BlogPost[];
  message?: string;
  pagination?: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
}

export interface BlogFilters {
  page?: number;
  limit?: number;
  category?: string;
  status?: 'draft' | 'published';
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

async function blogApiCall<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = localStorage.getItem('token');
  
  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || 'Something went wrong');
  }

  return data;
}

// Get all blog posts (public)
export const getPublicBlogs = async (filters?: BlogFilters): Promise<BlogResponse> => {
  const params = new URLSearchParams();
  
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
  }

  return await blogApiCall<BlogResponse>(`/blogs/public?${params.toString()}`);
};

// Get all blog posts (admin)
export const getAllBlogs = async (filters?: BlogFilters): Promise<BlogResponse> => {
  const params = new URLSearchParams();
  
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
  }

  return await blogApiCall<BlogResponse>(`/blogs?${params.toString()}`);
};

// Get blog post by slug (public)
export const getBlogBySlug = async (slug: string): Promise<BlogResponse> => {
  return await blogApiCall<BlogResponse>(`/blogs/public/${slug}`);
};

// Get blog post by ID (admin)
export const getBlogById = async (id: string): Promise<BlogResponse> => {
  return await blogApiCall<BlogResponse>(`/blogs/${id}`);
};

// Create new blog post
export const createBlog = async (blogData: Omit<BlogPost, '_id' | 'slug' | 'publishedDate' | 'createdBy' | 'createdAt' | 'updatedAt'>): Promise<BlogResponse> => {
  return await blogApiCall<BlogResponse>('/blogs', { 
    method: 'POST', 
    body: JSON.stringify(blogData) 
  });
};

// Update blog post
export const updateBlog = async (id: string, blogData: Partial<BlogPost>): Promise<BlogResponse> => {
  return await blogApiCall<BlogResponse>(`/blogs/${id}`, { 
    method: 'PUT', 
    body: JSON.stringify(blogData) 
  });
};

// Delete blog post
export const deleteBlog = async (id: string): Promise<{ success: boolean; message: string }> => {
  return await blogApiCall<{ success: boolean; message: string }>(`/blogs/${id}`, { 
    method: 'DELETE' 
  });
};

// Get blog categories
export const getBlogCategories = async (): Promise<{ success: boolean; data: string[] }> => {
  return await blogApiCall<{ success: boolean; data: string[] }>('/blogs/categories');
};

// Get related blog posts
export const getRelatedBlogs = async (slug: string, limit: number = 3): Promise<BlogResponse> => {
  return await blogApiCall<BlogResponse>(`/blogs/related/${slug}/${limit}`);
}; 